import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
import { API_BASE as API } from '../config';

// Ensure all axios requests send auth cookies
axios.defaults.withCredentials = true;

// Token storage key for sessionStorage fallback when cookies are blocked (e.g., iframes)
const TOKEN_KEY = 'cms_auth_token';

export function AuthProvider({ children }) {
  const [rawUser, setRawUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allTenants, setAllTenants] = useState([]);
  const [selectedTenants, setSelectedTenants] = useState([]);
  const selectedTenantsRef = useRef([]);
  // V2: Org switcher state for provider accounts
  const [userOrgs, setUserOrgs] = useState([]);
  const [activeOrgId, setActiveOrgIdRaw] = useState(() => {
    try { return localStorage.getItem('activeOrgId') || null; } catch { return null; }
  });
  const activeOrgRef = useRef(activeOrgId);
  const setActiveOrgId = (id) => {
    setActiveOrgIdRaw(id);
    activeOrgRef.current = id;
    try { if (id) localStorage.setItem('activeOrgId', id); else localStorage.removeItem('activeOrgId'); } catch {}
  };
  // Demo view mode: allows demo users to toggle between solo/provider views
  const [demoViewMode, setDemoViewMode] = useState(() => {
    try { return localStorage.getItem('demoViewMode') || null; } catch { return null; }
  });

  const DEMO_EMAILS = ['demo@allaboutclaims.com', 'demo-provider@allaboutclaims.com', 'viewer@demo.com', 'member@demo.com', 'manager@demo.com'];

  // Computed user: override account_type for demo view mode
  const user = rawUser && demoViewMode && DEMO_EMAILS.includes(rawUser.email)
    ? { ...rawUser, account_type: demoViewMode }
    : rawUser;

  const updateDemoViewMode = (mode) => {
    setDemoViewMode(mode);
    try { if (mode) localStorage.setItem('demoViewMode', mode); else localStorage.removeItem('demoViewMode'); } catch {}
  };

  // Operator context switching
  const defaultOpCtx = { accountType: null, tenantId: null, tenantName: null, orgId: null, orgName: null };
  const [operatorContext, setOperatorContext] = useState(() => {
    try {
      const saved = localStorage.getItem('operatorContext');
      return saved ? { ...defaultOpCtx, ...JSON.parse(saved) } : defaultOpCtx;
    } catch { return defaultOpCtx; }
  });
  const operatorContextRef = useRef(operatorContext);

  useEffect(() => {
    operatorContextRef.current = operatorContext;
    localStorage.setItem('operatorContext', JSON.stringify(operatorContext));
  }, [operatorContext]);

  const resetOperatorContext = useCallback(() => {
    setOperatorContext({ accountType: null, tenantId: null, tenantName: null, orgId: null, orgName: null });
  }, []);

  // Keep ref in sync for interceptor
  useEffect(() => {
    selectedTenantsRef.current = selectedTenants;
  }, [selectedTenants]);

  useEffect(() => {
    activeOrgRef.current = activeOrgId;
  }, [activeOrgId]);

  // Axios interceptor: transparently adds auth token, tenant_ids, org_id, and operator context
  useEffect(() => {
    const id = axios.interceptors.request.use((config) => {
      // Always attach Bearer token if available (fallback when cookies are blocked)
      const token = sessionStorage.getItem(TOKEN_KEY);
      if (token) {
        config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
      }

      const tenants = selectedTenantsRef.current;
      const orgId = activeOrgRef.current;
      const opCtx = operatorContextRef.current;
      const url = config.url || '';

      // Operator context: add X-Operator-Tenant header and optional org_id
      if (opCtx?.tenantId) {
        config.headers = { ...config.headers, 'X-Operator-Tenant': opCtx.tenantId };
        if (opCtx.orgId) {
          config.params = { ...config.params, org_id: opCtx.orgId };
        }
      }

      const isDataEndpoint = url.includes('/api/claims') || url.includes('/api/companies') || url.includes('/api/users') || url.includes('/api/audit-logs') || url.includes('/api/tenant/settings') || url.includes('/api/dashboard');
      // Callers can opt out of the auto-injected org filter by setting
      // `config.skipOrgFilter = true` (e.g. the Organizations management page
      // which always needs the cross-org list to manage assignments).
      const skipOrgFilter = config.skipOrgFilter === true;
      if (isDataEndpoint) {
        if (tenants.length > 0) {
          config.params = { ...config.params, tenant_ids: tenants.join(',') };
        }
        if (orgId && !skipOrgFilter) {
          config.params = { ...config.params, org_id: orgId };
        }
      }
      return config;
    });
    return () => {
      axios.interceptors.request.eject(id);
    };
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setRawUser(res.data);
    } catch {
      setRawUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch all tenants when user is super_admin (not for platform operators)
  useEffect(() => {
    if (user?.role === 'super_admin' && !user?.is_platform_operator) {
      axios.get(`${API}/tenants`, { withCredentials: true })
        .then((res) => setAllTenants(res.data))
        .catch(() => {});
    } else {
      setAllTenants([]);
      setSelectedTenants([]);
    }
  }, [user?.role, user?.tenant_id, user?.is_platform_operator]);

  // Fetch user's org memberships for provider accounts
  useEffect(() => {
    if (!user) return; // Don't clear state while still loading
    if (user.account_type === 'provider') {
      axios.get(`${API}/user/org-memberships`, { withCredentials: true })
        .then((res) => setUserOrgs(res.data))
        .catch(() => setUserOrgs([]));
    } else {
      setUserOrgs([]);
      setActiveOrgId(null);
    }
  }, [user?.account_type, user?.tenant_id]);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (res.data.token) sessionStorage.setItem(TOKEN_KEY, res.data.token);
    if (res.data.is_platform_operator) resetOperatorContext();
    setRawUser(res.data);
    // PostHog: identify user on login
    if (window.posthog && res.data.user_id) {
      window.posthog.identify(res.data.user_id, { email: res.data.email, name: res.data.name, account_type: res.data.account_type, role: res.data.role });
    }
    return res.data;
  };

  const tryDemo = async ({ view = 'provider', role = 'admin' } = {}) => {
    const res = await axios.post(`${API}/auth/try-demo`, { view, role }, { withCredentials: true });
    if (res.data.token) sessionStorage.setItem(TOKEN_KEY, res.data.token);
    updateDemoViewMode(view);
    setRawUser(res.data);
    if (window.posthog && res.data.user_id) {
      window.posthog.identify(res.data.user_id, { email: res.data.email, name: res.data.name, account_type: view, role: res.data.role });
      window.posthog.capture('demo_try_demo', { view, role });
    }
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data, { withCredentials: true });
    if (res.data.token) sessionStorage.setItem(TOKEN_KEY, res.data.token);
    setRawUser(res.data);
    // PostHog: identify user on register
    if (window.posthog && res.data.user_id) {
      window.posthog.identify(res.data.user_id, { email: res.data.email, name: res.data.name, account_type: res.data.account_type });
      window.posthog.capture('user_registered', { account_type: data.account_type || 'solo' });
    }
    return res.data;
  };

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleCallback = async (sessionId) => {
    const res = await axios.post(`${API}/auth/google/callback`, { session_id: sessionId }, { withCredentials: true });
    if (res.data.token) sessionStorage.setItem(TOKEN_KEY, res.data.token);
    if (res.data.is_platform_operator) resetOperatorContext();
    setRawUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    sessionStorage.removeItem(TOKEN_KEY);
    setRawUser(null);
    setSelectedTenants([]);
    setUserOrgs([]);
    setActiveOrgId(null);
    updateDemoViewMode(null);
    resetOperatorContext();
    // PostHog: reset on logout
    if (window.posthog) window.posthog.reset();
  };

  const hasRole = (...roles) => user && roles.includes(user.role);

  // ── Phase 3: scoped access helpers ────────────────────────────────────
  // Mirrors backend `get_user_scope`. A user is "unscoped" when they have
  // full reach inside their tenant: super_admin, platform operator, Solo
  // user (any role), Provider admin, OR a Provider user whose `org_access`
  // field has never been written (back-compat with pre-grandfather data).
  const isUnscoped = (() => {
    if (!user) return true;
    if (user.is_platform_operator || user.role === 'super_admin' || user.role === 'admin') return true;
    if (user.account_type !== 'provider') return true;
    if (user.org_access == null) return true; // missing field = grandfathered
    return false;
  })();

  // Set of org_ids the user can see. Returns null when unscoped (meaning "all").
  const allowedOrgIds = isUnscoped ? null : new Set(user?.org_access || []);

  /**
   * True when the user can see the given org_id. Unscoped users always pass.
   * `null` / `undefined` org_id is treated as "All Orgs" view → only allowed
   * if the user has access to >= 1 org.
   */
  const canAccessOrg = (org_id) => {
    if (isUnscoped) return true;
    if (!allowedOrgIds) return true;
    if (!org_id) return allowedOrgIds.size > 0;
    return allowedOrgIds.has(org_id);
  };

  /**
   * True when the user can see the given (org_id, company_name) pair. Used
   * to filter company dropdowns + entries on the Companies/AllEntries pages.
   * Manager + Admin + Unscoped users have full company reach inside their
   * allowed orgs; only Member/Viewer enforce per-org `company_access` lists.
   */
  const canAccessCompany = (org_id, company_name) => {
    if (isUnscoped) return true;
    if (!canAccessOrg(org_id)) return false;
    if (user?.role === 'manager') return true;  // managers see all companies in their orgs
    const ca = user?.company_access || {};
    const allowed = ca[org_id];
    if (!Array.isArray(allowed) || allowed.length === 0) return true; // empty = all in this org
    return allowed.includes(company_name);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, loginWithGoogle, processGoogleCallback, logout, hasRole, checkAuth,
      tryDemo,
      allTenants, selectedTenants, setSelectedTenants,
      userOrgs, activeOrgId, setActiveOrgId,
      operatorContext, setOperatorContext, resetOperatorContext,
      demoViewMode, setDemoViewMode: updateDemoViewMode,
      // Phase 3 scope helpers
      isUnscoped, allowedOrgIds, canAccessOrg, canAccessCompany,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
